import React, { useEffect, useState } from 'react';

import { confirmAppDialog } from '../../services/appDialogs';
import { ModalContent } from '../ModalContent';

import { ConnectionForm } from './ConnectionForm';
import { ConnectionStatusView } from './ConnectionStatusView';
import { useGitHubConnect } from './useGitHubConnect';

import './GitHubConnectModal.css';

interface GitHubConnectModalProps {
  isOpen: boolean;
  onClose: () => void;
  projectId?: string | null;
  onConnectedProject?: (projectId: string) => void;
}

export const GitHubConnectModal: React.FC<GitHubConnectModalProps> = ({
  isOpen,
  onClose,
  projectId,
  onConnectedProject,
}) => {
  const { isLoading, connectionStatus, connect, disconnect, sync, syncMessage } = useGitHubConnect(
    isOpen,
    projectId,
    onConnectedProject,
  );
  const [isChangingRepository, setIsChangingRepository] = useState(false);

  useEffect(() => {
    if (isOpen) setIsChangingRepository(false);
  }, [isOpen]);

  const handleDisconnect = async () => {
    if (!await confirmAppDialog({
      title: 'Disconnect repository?', message: 'Disconnect this GitHub repository from the project?',
      confirmLabel: 'Disconnect repository', cancelLabel: 'Keep connected', confirmVariant: 'danger',
    })) return;
    await disconnect();
  };

  if (!isOpen) return null;

  return (
    <div className="github-modal-overlay" onClick={onClose} role="presentation">
      <div className="github-modal-container" role="presentation">
        <ModalContent onClose={onClose} className="github-connection-modal">
          <div className="github-modal-content">
            <div className="github-modal-header">
              <h2>GitHub Connection</h2>
              <button type="button" className="github-modal-close" onClick={onClose} aria-label="Close GitHub connection">
                <i className="fa-solid fa-xmark" />
              </button>
            </div>
            {isLoading && !connectionStatus ? (
              <div>Loading...</div>
            ) : connectionStatus?.connected && !isChangingRepository ? (
              <ConnectionStatusView
                status={connectionStatus}
                isLoading={isLoading}
                onDisconnect={handleDisconnect}
                onChange={() => setIsChangingRepository(true)}
                onSync={sync}
                syncMessage={syncMessage}
              />
            ) : (
              <ConnectionForm
                isLoading={isLoading}
                onConnect={connect}
                onCancel={connectionStatus?.connected ? () => setIsChangingRepository(false) : onClose}
                initialRepoUrl={connectionStatus?.source_repo_url || connectionStatus?.repo_url}
                initialUsername={connectionStatus?.username}
                projectId={projectId}
                onProjectSelected={onConnectedProject}
              />
            )}
          </div>
        </ModalContent>
      </div>
    </div>
  );
};
